import { useEffect, useState } from "react";
import { DownloadSimple, X, DeviceMobile } from "@phosphor-icons/react";

/**
 * InstallPrompt
 * - Listens to `beforeinstallprompt` (Chrome / Edge / Android) and shows a
 *   compact bottom banner inviting the user to install Utheri to their home
 *   screen.
 * - For iOS Safari (no beforeinstallprompt), shows a one-line tip explaining
 *   the Share → Add to Home Screen flow.
 * - Dismissals are sticky in localStorage for 14 days so we don't nag.
 */
const STORAGE_KEY = "utheri.install.dismissedUntil";
const COOLDOWN_DAYS = 14;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  // iOS Safari
  // eslint-disable-next-line no-undef
  navigator.standalone === true;

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !/crios|fxios/i.test(navigator.userAgent);

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosTip, setIosTip] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissed = Number(localStorage.getItem(STORAGE_KEY) || 0);
    if (dismissed && Date.now() < dismissed) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS doesn't fire beforeinstallprompt — show a one-time tip instead
    if (isIOS()) {
      const t = setTimeout(() => setIosTip(true), 4000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", handler); };
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now() + COOLDOWN_DAYS * 86400_000));
    setShow(false); setIosTip(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setShow(false);
    setDeferred(null);
    if (outcome !== "accepted") {
      // user said "not now" — wait the cooldown before nagging again
      localStorage.setItem(STORAGE_KEY, String(Date.now() + COOLDOWN_DAYS * 86400_000));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  if (!show && !iosTip) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[60] bg-card border border-border rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3"
      data-testid="install-prompt"
      role="dialog"
      aria-label="Install Utheri app"
    >
      <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <DeviceMobile className="w-5 h-5" weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        {show ? (
          <>
            <p className="text-sm font-semibold leading-tight" data-testid="install-prompt-title">Install Utheri</p>
            <p className="text-xs text-muted-foreground leading-tight mt-0.5">Add to your home screen for quick contributions and offline statements.</p>
          </>
        ) : (
          <>
            <p className="text-sm font-semibold leading-tight">Add Utheri to your iPhone</p>
            <p className="text-xs text-muted-foreground leading-tight mt-0.5">Tap <strong>Share</strong> → <strong>Add to Home Screen</strong>.</p>
          </>
        )}
      </div>
      {show && (
        <button
          onClick={install}
          data-testid="install-prompt-install-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-semibold active:scale-95 shrink-0"
        >
          <DownloadSimple className="w-3.5 h-3.5" weight="bold" /> Install
        </button>
      )}
      <button
        onClick={dismiss}
        data-testid="install-prompt-dismiss-btn"
        className="p-1 rounded-lg hover:bg-secondary text-muted-foreground shrink-0"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
